/**
 * The authored campaign chain — proves the data-driven format the town
 * boards also feed. v1 ships a single introductory quest; more slot in as
 * registry entries with `camp:<slug>` ids.
 *
 * `camp:first-steps` is town-relative on purpose: its `elder`/`keeper`
 * targets resolve against whatever settlement the player accepts it at, and
 * because stage 1 (talk) and stage 3 (deliver) name the SAME role, they
 * resolve to the SAME persona — you bring the ore back to the very elder who
 * sent you. Surfaced atop every board until completed.
 */
import type { QuestDef } from "./types.js";

export const HOME_ARRIVAL_QUEST_ID = "camp:arrival";
export const PROLOGUE_QUEST_ID = "camp:prologue";

export const CAMPAIGN: QuestDef[] = [
  {
    // The SOUL kickoff crawl (docs/design/soul-kickoff.md). Auto-started at a
    // prologue-stage join, never board-offered, and completed server-side by
    // founding-resident bootstrap — establishing the homestead IS the turn-in.
    id: PROLOGUE_QUEST_ID,
    category: "campaign",
    scope: "prologue",
    title: "The Long Walk Home",
    blurb: "The cache gave up a SOUL — and it chose your hand. Get it home.",
    stages: [
      {
        // Kill + salvage run in PARALLEL (one stage): the weavers ARE the
        // salvage source, so their drops must count while the fight is on.
        objectives: [
          { kind: "kill", n: 4, text: "Clear the weavers from the cache approach" },
          { kind: "collect", n: 3, item: "scrap-alloy", text: "Salvage 3 Scrap Alloy from the wreckage" },
        ],
        journal: "The path back out of the cache should be clear. It isn't — they're back. Newer looking.",
      },
      {
        objectives: [{ kind: "goto", n: 1, site: { here: true }, text: "Walk home" }],
        journal: "The bikes are gone. Stolen, probably. Nothing for it — walk home with your haul.",
      },
    ],
    reward: { credits: 150, items: [{ id: "medkit", n: 2 }] },
  },
  {
    id: HOME_ARRIVAL_QUEST_ID,
    category: "campaign",
    scope: "home-arrival",
    title: "A Light in the Dust",
    blurb: "Mara Venn needs a field reading before the capital can clear its outer approaches.",
    stages: [
      {
        objectives: [{ kind: "talk", n: 1, npc: { canon: "mara-venn" }, text: "Speak with Mara Venn" }],
        journal: "Find Mara Venn at the capital's civic hall.",
      },
      {
        objectives: [{ kind: "talk", n: 1, npc: { canon: "sable-rook" }, text: "Meet Sable Rook" }],
        journal: "Meet Sable Rook at the capital's field post.",
      },
      {
        objectives: [{ kind: "collect", n: 3, item: "ore-ferrite", text: "Mine 3 Ferrite Ore" }],
        journal: "Collect three Ferrite samples from a nearby seam.",
      },
      {
        objectives: [{ kind: "deliver", n: 3, item: "ore-ferrite", npc: { canon: "sable-rook" }, text: "Deliver the samples to Sable" }],
        journal: "Return the Ferrite samples to Sable Rook.",
      },
    ],
    reward: { credits: 500, items: [{ id: "medkit", n: 1 }] },
  },
  {
    id: "camp:first-steps",
    category: "campaign",
    title: "First Steps",
    blurb: "An elder wants to size you up before the town trusts you with real work.",
    stages: [
      {
        objectives: [
          { kind: "talk", n: 1, npc: { role: "elder", town: "here" }, text: "Speak with the town elder" },
        ],
        journal: "Find the elder and hear them out.",
      },
      {
        objectives: [
          { kind: "collect", n: 3, item: "ore-ferrite", text: "Mine 3 Ferrite Ore" },
        ],
        journal: "Mine three units of Ferrite Ore from a nearby seam.",
      },
      {
        objectives: [
          { kind: "deliver", n: 3, item: "ore-ferrite", npc: { role: "elder", town: "here" }, text: "Deliver 3 Ferrite Ore to the elder" },
        ],
        journal: "Bring the ore back to the elder who sent you.",
      },
    ],
    reward: { credits: 400, items: [{ id: "medkit", n: 1 }] },
  },
];

/** Look up an authored campaign def by id. */
export function campaignQuest(id: string): QuestDef | null {
  return CAMPAIGN.find((q) => q.id === id) ?? null;
}
